/**
 * The message-wording editor's draft, as pure functions — the same split `menu-draft.js` and
 * `task-draft.js` make, and for the same reason: a form that rewrites a live preview on every
 * keystroke and has to agree with a server about every rule has more states than a mounted
 * test can comfortably reach.
 *
 * THREE SHAPES, and keeping them apart is the whole point of this file:
 *
 *   • THE DRAFT binds to the form. Every button carries a local `key` (never sent) so `v-for`
 *     survives a removal, and `samples` is a plain array whose length follows the body's
 *     placeholders.
 *   • THE WIRE SHAPE is what `Api::V1::Accounts::WasloMessageTemplatesController` permits.
 *   • THE PREVIEW SHAPE is what Meta itself returns for an approved template — `components[]`
 *     with HEADER / BODY / FOOTER / BUTTONS — because that is what
 *     `components-next/template-preview/TemplatePreview.vue` already knows how to render. The
 *     draft is projected onto Meta's own shape rather than a new renderer being written for it,
 *     so what the owner sees while typing is produced by the same component that renders the
 *     template after Meta approves it. Nothing else can keep those two in step.
 *
 * A FOURTH DIRECTION EXISTS NOW: `templateToDraft`, which turns a template Meta already holds
 * back into a draft so it can be EDITED. Meta replaces all components on an edit, so a field
 * this function fails to read back is a field the owner silently loses the moment she fixes a
 * typo. That makes it the most dangerous function in the file, and it is asserted field by field.
 *
 * EVERY NUMBER AND PATTERN BELOW IS META'S, restated from `Waslo::TemplateBuilder`
 * (`deploy/branding/templates/template_builder.rb`) and `Waslo::TemplateButtons`. The server is
 * the boundary and decides; this half exists so an owner is told before she presses the button,
 * not after Meta answers. Keep the two in step — a rule that is only here is a rule a paste into
 * the field can skip, and a rule that is only there arrives as a field error with no field.
 */

export const CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];

/** Meta's two placeholder formats. Positional is Graph's default and this editor's. */
export const PARAMETER_FORMATS = ['POSITIONAL', 'NAMED'];

/**
 * The button types this editor AUTHORS.
 *
 * Four, not sixteen. Meta's vocabulary also has OTP, FLOW, MPM, SPM, CATALOG, VOICE_CALL,
 * ORDER_DETAILS and several that appear only in its webhook enum — all of them are READ and named
 * (`template-facts.js`), and none is offered here, because each needs something an SMB does not
 * have (a published Flow, a product catalogue, an Android signing hash) or a send path Chatwoot
 * does not have. VOICE_CALL additionally needs WhatsApp Calling, which is Chatwoot Enterprise and
 * therefore off-limits to Waslo by owner rule.
 *
 * Offering one would cost the owner a template slot and a review cycle to discover at send time
 * that it cannot be sent.
 */
export const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE'];

/** Meta's per-type caps. Over one of them it refuses the whole submission. */
export const BUTTON_CAPS = {
  QUICK_REPLY: 10,
  URL: 2,
  PHONE_NUMBER: 1,
  COPY_CODE: 1,
};

/** "Templates can have a combination of up to 10 button components in total." */
export const BUTTON_TOTAL_CAP = 10;

/**
 * Above this, WhatsApp delivers the first two and hides the rest behind a "See all options" tap
 * — and a template mixing quick replies with other buttons, or carrying four or more, cannot be
 * viewed on WhatsApp Desktop at all. Both are Meta's own words and both are worth telling an
 * owner while she is still adding buttons.
 */
export const BUTTONS_SHOWN_WITHOUT_TAP = 3;

export const LIMITS = Object.freeze({
  NAME: 512,
  BODY: 1024,
  HEADER: 60,
  FOOTER: 60,
  BUTTON_TEXT: 25,
  URL: 2000,
  PHONE_NUMBER: 20,
  // 20, not 15: Meta's limited-time-offer page still says 15 while its coupon page and its
  // component reference both say 20.
  COPY_CODE: 20,
  SAMPLE: 200,
  PARAM_NAME: 60,
  CODE_EXPIRY_MIN: 1,
  CODE_EXPIRY_MAX: 90,
});

/**
 * How long WhatsApp keeps retrying delivery, offered as the handful of spans an owner actually
 * means rather than a box for a number of seconds.
 *
 * THE RANGES ARE META'S AND THEY ARE PER CATEGORY: authentication 30s–15min, utility 30s–12h,
 * marketing 12h–30 days. MARKETING IS EMPTY ON PURPOSE — Meta documents its range as settable
 * through the Marketing Messages API only, not the Cloud API this product uses, so a control
 * here would silently do nothing. `-1` is Meta's shorthand for the 30-day maximum.
 */
export const TTL_CHOICES = Object.freeze({
  UTILITY: [1800, 7200, 43200, -1],
  AUTHENTICATION: [120, 300, 600, 900],
  MARKETING: [],
});

/**
 * Meta's actual bounds, which are WIDER than the shortcuts above — and it is the bounds that
 * decide, not the shortcuts.
 *
 * The distinction is not pedantry. A template authored in Meta's own manager can carry any value
 * in range (say 3600 seconds), `templateToDraft` reads it back, and a rule written against the
 * four offered shortcuts would then REFUSE THE OWNER'S SAVE over a number she never chose and
 * cannot see. The shortcuts are a convenience for writing; the range is the rule.
 */
export const TTL_RANGES = Object.freeze({
  UTILITY: [30, 43200],
  AUTHENTICATION: [30, 900],
});

/** `-1` is Meta's shorthand for its 30-day maximum, outside every range above. */
export const TTL_MAX_SENTINEL = -1;

export const ttlAllowed = (category, seconds) => {
  const range = TTL_RANGES[String(category ?? '').toUpperCase()];
  if (!range) return false;
  const value = Number(seconds);
  if (value === TTL_MAX_SENTINEL) return true;
  return Number.isFinite(value) && value >= range[0] && value <= range[1];
};

/** Meta: lowercase letters, digits and underscores only. */
export const NAME_PATTERN = /^[a-z0-9_]+$/;
/** `en`, `ar`, `en_US`. Meta matches the language EXACTLY, so a hyphen is not a near miss. */
export const LANGUAGE_PATTERN = /^[a-z]{2,3}(?:_[A-Z]{2})?$/;
/**
 * A NAMED placeholder, as Meta spells it — and WIDER than Meta's own sentence, on purpose.
 *
 * Meta: "Parameters using the named format must be unique, single strings, composed of lowercase
 * characters and underscores, wrapped in double curly brackets, for example, `{{first_name}}`."
 * No digits. `0-9` stays in this pattern anyway, and `nameWithDigit` WARNS about one instead,
 * for two reasons that both have to hold:
 *
 *   1. Meta documents the allowed SET but publishes no rejection reason saying a digit is
 *      refused. Turning an allow-list into a hard refusal is a guess, and a wrong one locks an
 *      owner out of `{{order_2}}` — a name she may be perfectly entitled to use.
 *   2. `Waslo::TemplateBuilder::NAMED_PATTERN` accepts digits, and it is the boundary. A client
 *      stricter than its server is the same defect wearing a hat (`template_rules.rb`'s own
 *      header), and that exact divergence has already locked an owner out of her own template.
 *
 * So the pair stays level and the owner is told, not stopped.
 */
export const NAMED_PATTERN = /^[a-z][a-z0-9_]*$/;
/**
 * A character inside `{{…}}` that is neither a letter, a digit nor an underscore — the class
 * Meta's template-review page names: "Variable parameters contain special characters such as a
 * `#`, `$`, or `%`."
 *
 * Whitespace is excluded deliberately. `{{order id}}` is someone reaching for a NAME in a
 * numbered draft, and `BODY_NAMED_BLANK` says exactly that; talking to her about signs would be
 * answering a question she did not ask.
 */
export const SYMBOL_IN_BLANK = /[^A-Za-z0-9_\s]/;
/** Loose on purpose, so a placeholder of the WRONG format is SEEN and refused, not ignored. */
const VARIABLE_PATTERN = /\{\{\s*([^}]*?)\s*\}\}/g;

let nextButtonKey = 0;
const buttonKey = () => {
  nextButtonKey += 1;
  return nextButtonKey;
};

const text = value => (typeof value === 'string' ? value : '');
const trimmed = value => text(value).trim();

/**
 * P-4.9: an Arabic-Indic or Persian digit typed INSIDE a blank is the ASCII digit WhatsApp expects,
 * as she types: `{{١}}` is `{{1}}`. Digits in her words are hers and stay. A blank still being typed
 * (`{{١`) is left until it closes. The server folds the same way (`template_builder.rb`
 * `fold_blank_digits`), so a client and a server cannot disagree about what a blank is.
 */
const CLOSED_BLANK = /\{\{[^}]*\}\}/g;
export const foldBlankDigits = value =>
  text(value).replace(CLOSED_BLANK, blank =>
    blank.replace(/[٠-٩۰-۹]/g, digit => {
      const code = digit.charCodeAt(0);
      return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660);
    })
  );

export const buttonRow = (over = {}) => ({
  key: buttonKey(),
  type: 'QUICK_REPLY',
  text: '',
  url: '',
  urlSample: '',
  phoneNumber: '',
  code: '',
  ...over,
});

export const blankDraft = () => ({
  name: '',
  // Arabic is what a Kuwaiti customer reads, so it is the default rather than a neutral blank
  // the owner has to discover she must fill in.
  language: 'ar',
  category: 'UTILITY',
  parameterFormat: 'POSITIONAL',
  header: '',
  headerSample: '',
  body: '',
  footer: '',
  samples: [],
  buttons: [],
  /** '' means "WhatsApp's own default", which is the honest resting state. */
  ttl: '',
  /** Authentication templates only. */
  codeExpirationMinutes: '',
  addSecurityRecommendation: true,
});

export const isAuthentication = draft =>
  trimmed(draft?.category).toUpperCase() === 'AUTHENTICATION';

export const isNamed = draft =>
  trimmed(draft?.parameterFormat).toUpperCase() === 'NAMED';

/** Every `{{…}}` in a string, in the order Meta will substitute them. */
export const placeholders = value => {
  const found = text(value).match(VARIABLE_PATTERN) || [];
  return found.map(match => match.replace(/[{}]/g, '').trim());
};

/**
 * The distinct placeholder numbers, in first-seen order. A non-numeric placeholder becomes
 * `null` so it can be NAMED as the problem — a silently dropped one reaches Meta as literal
 * text inside a paid message.
 */
export const placeholderNumbers = value => {
  const seen = [];
  placeholders(value).forEach(raw => {
    const number = /^\d+$/.test(raw) ? Number(raw) : null;
    if (!seen.includes(number)) seen.push(number);
  });
  return seen;
};

/** The distinct placeholder NAMES, in first-seen order — the NAMED format's equivalent. */
export const placeholderNames = value => [...new Set(placeholders(value))];

/**
 * A `{{` or a `}}` with no partner. Meta publishes the refusal in those words: "Variable
 * parameters are missing or have mismatched curly braces."
 *
 * ── THE ONE MISTAKE EVERY OTHER RULE IN THIS FILE IS BLIND TO ───────────────────────
 *
 * `VARIABLE_PATTERN` matches whole pairs only, and that looseness is deliberate and documented
 * above it: a placeholder of the WRONG FORMAT must still be SEEN so it can be named. But a
 * placeholder of the wrong SHAPE is not seen at all. `Hello {{1}` carries no placeholder as far
 * as `placeholders`, `placeholderNumbers`, `sampleCount`, `validateBlanks` and the preview are
 * concerned — so nothing asks for an example, nothing refuses it, and the preview renders it
 * exactly as typed. It is not a variable and it is not an error: it is LITERAL TEXT, and it
 * ships that way inside a message the business has paid to send.
 *
 * The whole pairs are removed first and whatever double brace is left over is the problem. That
 * order is what keeps the loose matching intact — a wrongly-numbered or wrongly-named blank is
 * still a whole pair, still seen, and still refused by the rule that names IT rather than by
 * this one.
 */
export const unpairedBraces = value =>
  /\{\{|\}\}/.test(text(value).replace(VARIABLE_PATTERN, ''));

/**
 * A `{{` that is still the last thing she typed — `Hello {{` on the way to `Hello {{1}}`.
 *
 * ── WHY A SECOND BRACE FUNCTION EXISTS ──────────────────────────────────────────────
 *
 * `unpairedBraces` is the RULE and it is right: `Hello {{1}` reaches Meta as literal brackets
 * inside a paid message. But this screen now checks its rules on every keystroke, and every
 * blank ever written passes through `{{` on its way to `{{1}}`. Firing the rule there would
 * paint the field ruby in the middle of typing the most ordinary thing on the page, and a
 * message that is wrong for a third of a second teaches an owner to ignore the colour.
 *
 * So the rule is held — never weakened — while the only unpaired brace is the one at the end of
 * what she has typed. The moment she types anything after it that is not a closing pair, the
 * brace is no longer "the thing being typed" and the rule fires. `validateDraft` does not call
 * this at all: on Save the strict rule is the only rule, so a value can never be SAVED past it.
 */
export const openingBlank = value =>
  /\{\{[^{}]*$/.test(text(value).replace(VARIABLE_PATTERN, ''));

/**
 * Meta: "The message template cannot start or end with a parameter (dangling parameters are not
 * allowed)."
 *
 * `{{1}}, your order is ready` is the most natural sentence a shop owner writes, and it is
 * refused — up to 24 hours later, in a sentence written for a developer. Nothing in this editor
 * or on the server saw it until now.
 */
export const danglingBlank = value => {
  const body = text(value).trim();
  if (!body) return false;
  return body.startsWith('{{') || body.endsWith('}}');
};

const componentText = (template, wanted) => {
  const parts = Array.isArray(template?.components) ? template.components : [];
  const part = parts.find(
    one => String(one?.type ?? '').toUpperCase() === wanted
  );
  return trimmed(part?.text);
};

/**
 * The template Meta already holds that this draft repeats word for word, or `null`.
 *
 * Meta: "If a template is submitted with the same wording in the body and footer of an existing
 * template, the duplicate template will be rejected" — and "This check does not apply to
 * templates categorized as `AUTHENTICATION`", which is why both sides of the comparison skip
 * that category rather than only the draft.
 *
 * ── WHY IT IS A FUNCTION AND NOT A RULE ─────────────────────────────────────────────
 *
 * It is NOT called from `validateDraft`, on purpose. Every rule in that function is also a rule
 * in `Waslo::TemplateRules`, because a client stricter than its server is the same defect
 * wearing a hat. This one cannot be: the server's only free copy of the library is the blob
 * Chatwoot's sync mirrors onto the channel, which it does not own and which can hold a template
 * deleted at Meta minutes ago — refusing a create against that is a lock-out, the very thing
 * this change exists to remove. So this warns and never refuses, and the two sides stay level.
 *
 * NOT VERIFIED: Meta's page says nothing about LANGUAGE. Two rows of the same name in different
 * languages are separate templates, so a matching body across them is possible in principle;
 * the rule is applied as Meta writes it — on the wording alone — and it warns rather than
 * refuses precisely because that edge is unconfirmed.
 *
 * @param draft the draft being written.
 * @param templates the library the screen already holds (`api/templates.js` DTOs).
 * @param currentId the id being EDITED. A template is not its own duplicate.
 * @returns the first matching template, or null.
 */
export const duplicateOf = (draft, templates, currentId = null) => {
  if (isAuthentication(draft)) return null;
  const body = trimmed(draft?.body);
  if (!body) return null;
  const footer = trimmed(draft?.footer);
  const mine = currentId === null ? null : String(currentId);
  const found = (Array.isArray(templates) ? templates : []).find(one => {
    if (mine !== null && String(one?.id) === mine) return false;
    if (String(one?.category ?? '').toUpperCase() === 'AUTHENTICATION') {
      return false;
    }
    const theirs = componentText(one, 'BODY') || trimmed(one?.body);
    return theirs === body && componentText(one, 'FOOTER') === footer;
  });
  return found ?? null;
};

/** How many example values Meta wants for this string, in this draft's format. */
export const sampleCount = (body, draft = null) => {
  if (draft && isNamed(draft)) return placeholderNames(body).length;
  return placeholderNumbers(body).filter(number => number !== null).length;
};

/**
 * The draft with its `samples` array the length the body now needs — called on every body
 * change, so a placeholder removed does not leave a stale example behind and one added does
 * not read from `undefined`.
 */
export const fitSamples = draft => {
  const wanted = sampleCount(draft.body, draft);
  const samples = Array.from({ length: wanted }, (_, index) =>
    text(draft.samples?.[index])
  );
  return { ...draft, samples };
};

const buttonToWire = button => {
  const wire = { type: button.type };
  // COPY_CODE carries no label: "Copy code button text cannot be customized" — Meta localises
  // it. Sending one is a refusal.
  if (button.type !== 'COPY_CODE') wire.text = trimmed(button.text);
  if (button.type === 'URL') {
    wire.url = trimmed(button.url);
    const sample = trimmed(button.urlSample);
    if (sample) wire.url_sample = sample;
  }
  if (button.type === 'PHONE_NUMBER') {
    wire.phone_number = trimmed(button.phoneNumber);
  }
  if (button.type === 'COPY_CODE') wire.code = trimmed(button.code);
  return wire;
};

export const draftToWire = draft => {
  const wire = {
    name: trimmed(draft.name),
    language: trimmed(draft.language),
    category: trimmed(draft.category).toUpperCase(),
    parameter_format:
      trimmed(draft.parameterFormat).toUpperCase() || 'POSITIONAL',
  };
  if (draft.ttl !== '' && draft.ttl !== null && draft.ttl !== undefined) {
    wire.message_send_ttl_seconds = Number(draft.ttl);
  }
  // A sign-in code template has no wording of its own: Meta writes the body itself, in every
  // language, and refuses one you wrote. Sending the form's leftover body would be a refusal.
  if (isAuthentication(draft)) {
    wire.add_security_recommendation = draft.addSecurityRecommendation === true;
    if (draft.codeExpirationMinutes !== '') {
      wire.code_expiration_minutes = Number(draft.codeExpirationMinutes);
    }
    return wire;
  }
  wire.header = trimmed(draft.header);
  wire.header_sample = trimmed(draft.headerSample);
  wire.body = trimmed(draft.body);
  wire.footer = trimmed(draft.footer);
  wire.samples = (draft.samples ?? []).map(sample => trimmed(sample));
  wire.buttons = (draft.buttons ?? []).map(buttonToWire);
  return wire;
};

/** An edit adds the one flag the server needs to know it may re-price the template. */
export const draftToEditWire = (draft, changeCategory) => ({
  ...draftToWire(draft),
  change_category: changeCategory === true,
});

const bodyExample = draft => {
  const wanted = sampleCount(draft.body, draft);
  if (wanted === 0) return null;
  const values = Array.from({ length: wanted }, (_, index) =>
    trimmed(draft.samples?.[index])
  );
  if (!isNamed(draft)) return { body_text: [values] };
  return {
    body_text_named_params: placeholderNames(draft.body)
      .slice(0, wanted)
      .map((name, index) => ({ param_name: name, example: values[index] })),
  };
};

const headerExample = draft => {
  const sample = trimmed(draft.headerSample);
  if (!isNamed(draft)) return { header_text: [sample] };
  return {
    header_text_named_params: [
      { param_name: placeholderNames(draft.header)[0] ?? '', example: sample },
    ],
  };
};

const previewButton = (button, labels) => {
  const wire = buttonToWire(button);
  if (button.type !== 'COPY_CODE') return wire;
  // The LABEL only, and only in the preview — it is never sent. WhatsApp draws its own.
  return { ...wire, text: labels.copyCode ?? '' };
};

/**
 * What WhatsApp will actually send for a sign-in code, in the owner's own language.
 *
 * The body is not hers — Meta composes it — so the preview shows META'S wording rather than an
 * empty bubble or, worse, whatever is left in the message box. The sentences arrive translated
 * from the caller; inventing them here would put English in an Arabic preview.
 */
const authenticationPreview = (draft, labels) => {
  const lines = [labels.authBody ?? ''];
  if (draft.addSecurityRecommendation === true && labels.authSecurity) {
    lines.push(labels.authSecurity);
  }
  const components = [{ type: 'BODY', text: lines.filter(Boolean).join(' ') }];
  if (draft.codeExpirationMinutes !== '' && labels.authExpiry) {
    components.push({ type: 'FOOTER', text: labels.authExpiry });
  }
  components.push({
    type: 'BUTTONS',
    buttons: [{ type: 'OTP', text: labels.copyCode ?? '' }],
  });
  return {
    name: trimmed(draft.name),
    language: trimmed(draft.language),
    category: 'AUTHENTICATION',
    parameter_format: 'POSITIONAL',
    components: components,
  };
};

/**
 * The draft as Meta's own template object, for `TemplatePreview`.
 *
 * `example` blocks are included because `TemplateNormalizer.extractWhatsAppVariables` reads them
 * to seed its variable map — without them the preview shows `[1]` for a placeholder the owner has
 * already given a value for. It reads the POSITIONAL key or the NAMED one depending on
 * `parameter_format`, which is why that field is set here rather than assumed.
 *
 * @param labels {{copyCode?: string, authBody?: string, authSecurity?: string,
 *   authExpiry?: string}} translated strings the preview needs and the draft does not carry.
 *   `copyCode` is load-bearing: `CallToActionTemplate` renders `button.text || 'Button'`, and a
 *   copy-code button has no text by Meta's rule — so without it an Arabic owner reads the English
 *   word "Button" in her own preview. `auth*` are how WhatsApp itself words a sign-in code, which
 *   is the only honest preview for a body Meta writes.
 */
export const draftToPreview = (draft, labels = {}) => {
  if (isAuthentication(draft)) return authenticationPreview(draft, labels);

  const components = [];
  const header = trimmed(draft.header);
  if (header) {
    const part = { type: 'HEADER', format: 'TEXT', text: header };
    if (placeholders(header).length) part.example = headerExample(draft);
    components.push(part);
  }

  const body = { type: 'BODY', text: text(draft.body) };
  const example = bodyExample(draft);
  if (example) body.example = example;
  components.push(body);

  const footer = trimmed(draft.footer);
  if (footer) components.push({ type: 'FOOTER', text: footer });

  const buttons = (draft.buttons ?? []).map(button =>
    previewButton(button, labels)
  );
  if (buttons.length) components.push({ type: 'BUTTONS', buttons: buttons });

  return {
    name: trimmed(draft.name),
    language: trimmed(draft.language),
    category: trimmed(draft.category).toUpperCase(),
    parameter_format: isNamed(draft) ? 'NAMED' : 'POSITIONAL',
    components: components,
  };
};

/**
 * `{ '1': 'Sara' }` or `{ order_id: 'A-1042' }` for `TemplatePreview`'s `variables` prop. An
 * empty sample is left OUT rather than passed as `''`: the preview renders `[1]` for a variable
 * it has no value for, which is the honest thing to show for a blank not filled in yet.
 */
export const previewVariables = draft => {
  const values = {};
  if (isNamed(draft)) {
    placeholderNames(draft.body).forEach((name, index) => {
      const value = trimmed(draft.samples?.[index]);
      if (value) values[name] = value;
    });
  } else {
    placeholderNumbers(draft.body)
      .filter(number => number !== null)
      .forEach(number => {
        const value = trimmed(draft.samples?.[number - 1]);
        if (value) values[String(number)] = value;
      });
  }
  const headerValue = trimmed(draft.headerSample);
  const headerBlank = placeholders(draft.header)[0];
  if (headerValue && headerBlank) values[headerBlank] = headerValue;
  return values;
};

const errorKey = name => `WASLO.TEMPLATES.EDITOR.ERRORS.${name}`;

/**
 * The literal `{{1}}` / `{{2}}`, passed as INTERPOLATION VALUES rather than written into the
 * messages.
 *
 * A brace is vue-i18n's own interpolation syntax, so a message containing `{1}` renders as
 * nothing at all. Here the examples are the whole point of the sentence ("number your blanks
 * {{1}}, then {{2}}"), so they ride in as values, which vue-i18n substitutes without
 * re-processing. Every message in this file's error table may therefore use `{blank}`, `{next}`
 * and `{named}` freely — `say` passes them to all of them, whether they read them or not.
 */
export const BLANK_SAMPLES = Object.freeze({
  blank: '{{1}}',
  next: '{{2}}',
  named: '{{order_id}}',
});

const say = (t, name, params = {}) =>
  t(errorKey(name), { ...BLANK_SAMPLES, ...params });

const validateName = (wire, t) => {
  if (!wire.name) return say(t, 'NAME_BLANK');
  if (wire.name.length > LIMITS.NAME) return say(t, 'NAME_LONG');
  if (!NAME_PATTERN.test(wire.name)) return say(t, 'NAME_INVALID');
  return '';
};

/**
 * Both formats, and the mismatch is what this catches.
 *
 * ── TWO MISTAKES SHARED ONE SENTENCE, AND IT NAMED THE WRONG ONE ────────────────────
 *
 * `{{Customer}}` and `{{#1}}` are both "not a number" in a numbered draft, and both used to
 * answer with `BODY_NAMED_BLANK` — "you are using numbered blanks, switch to named ones if you
 * would rather use words". For the first that is the diagnosis. For the second it describes a
 * name the owner never wrote: she typed a `#`, and switching to named blanks would not help her,
 * because `{{#1}}` is not a valid name either. Meta lists the real thing as its own rejection
 * reason ("Variable parameters contain special characters such as a `#`, `$`, or `%`").
 *
 * Same refusal, two codes. `Waslo::TemplateRules#number_refusal` makes the same split with the
 * same test, so the server's `variable_has_symbol` and this arrive as one sentence.
 */
const validateBlanks = (value, draft, t) => {
  if (isNamed(draft)) {
    const bad = placeholderNames(value).some(
      name => !NAMED_PATTERN.test(name) || name.length > LIMITS.PARAM_NAME
    );
    return bad ? say(t, 'BODY_NOT_A_NAME') : '';
  }
  const numbers = placeholderNumbers(value);
  if (numbers.includes(null)) {
    // A sign anywhere among them decides: its sentence is the more specific of the two, and one
    // `#` is enough to have caused the refusal.
    return placeholderNames(value).some(name => SYMBOL_IN_BLANK.test(name))
      ? say(t, 'BLANK_SYMBOL')
      : say(t, 'BODY_NAMED_BLANK');
  }
  const ordered = [...numbers].sort((a, b) => a - b);
  return ordered.every((number, index) => number === index + 1)
    ? ''
    : say(t, 'BODY_SEQUENCE');
};

/**
 * The two shape rules, before the format rules — because until the braces pair up there is no
 * placeholder to have a format, and a dangling one is refused whatever its format.
 *
 * `Waslo::TemplateRules#body` runs the same pair in the same order. It reports the dangling
 * blank ALONGSIDE the numbering errors rather than instead of them, because its channel is a
 * list; this one returns a single sentence per field, so the first is the one that shows.
 */
const validateBody = (wire, draft, t) => {
  if (!wire.body) return say(t, 'BODY_BLANK');
  if (wire.body.length > LIMITS.BODY) return say(t, 'BODY_LONG');
  if (unpairedBraces(wire.body)) return say(t, 'BLANK_BRACES');
  if (danglingBlank(wire.body)) return say(t, 'BODY_DANGLING');
  return validateBlanks(wire.body, draft, t);
};

/**
 * One example value per blank, present and short enough. THE CONTENT OF ONE IS NOT CHECKED, AND
 * THIS IS THE NOTE THAT SAYS WHY — because the next person to read Meta's rejection list will
 * reach for the rule that breaks every price template, and one already did.
 *
 * Meta's template-review page lists "Variable parameters contain special characters such as a
 * `#`, `$`, or `%`" among the reasons it refuses a template. Read straight down, that looks like
 * an unenforced rule sitting right here, one commit from being closed.
 *
 * IT IS ABOUT THE PARAMETER, NOT ITS EXAMPLE. Meta's fundamentals page keeps the two in separate
 * sentences — the parameter is the thing "wrapped in double curly brackets", and separately "you
 * must include an example value for each parameter". A `#` inside `{{…}}` is a malformed blank; a
 * `#` in the value that gets substituted INTO one is an order number.
 * https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/overview
 *
 * AND THE BLANK ITSELF IS ALREADY CLOSED, in both formats: `{{#name}}` fails `NAMED_PATTERN`
 * (`BODY_NOT_A_NAME`) and `{{#1}}` is not a number (`BLANK_SYMBOL`). The rejection reason has no
 * third surface left to apply to, so there is nothing here to add.
 *
 * What adding it would COST is the whole feature: `$20`, `50% off` and `#A-1042` are the three
 * most ordinary example values a shop has, and if Meta refused templates carrying them no price
 * template could ever have been approved. Refusing them would be the lock-out this file's header
 * warns about, with the browser and the server confidently agreeing on a rule Meta never wrote.
 */
const validateSamples = (wire, draft, t) => {
  const wanted = sampleCount(wire.body, draft);
  if (wanted === 0) return '';
  const values = wire.samples.slice(0, wanted);
  if (values.length < wanted || values.some(value => !value)) {
    return say(t, 'SAMPLES_MISSING');
  }
  if (values.some(value => value.length > LIMITS.SAMPLE)) {
    return say(t, 'SAMPLES_LONG');
  }
  return '';
};

/** One blank allowed in the line above, with an example. None at all in the line below. */
const validateHeader = (wire, draft, t) => {
  if (!wire.header) return '';
  if (wire.header.length > LIMITS.HEADER) return say(t, 'HEADER_LONG');
  if (unpairedBraces(wire.header)) return say(t, 'BLANK_BRACES');
  const found = placeholders(wire.header);
  if (found.length === 0) return '';
  if (new Set(found).size > 1) return say(t, 'HEADER_ONE_BLANK');
  return validateBlanks(wire.header, draft, t);
};

const validateFooter = (wire, t) => {
  if (!wire.footer) return '';
  if (wire.footer.length > LIMITS.FOOTER) return say(t, 'FOOTER_LONG');
  // Before the "no blanks here" rule, not after it: a half-written blank is not a blank, so
  // that rule cannot see it, and the line below is exactly where one would ship as brackets.
  if (unpairedBraces(wire.footer)) return say(t, 'BLANK_BRACES');
  if (placeholders(wire.footer).length) return say(t, 'FOOTER_NO_BLANKS');
  return '';
};

const validateUrlButton = (button, draft, t) => {
  if (!button.url) return say(t, 'BUTTON_URL_BLANK');
  if (button.url.length > LIMITS.URL) return say(t, 'BUTTON_URL_LONG');
  if (!/^https?:\/\/\S+$/.test(button.url)) return say(t, 'BUTTON_URL_INVALID');
  if (unpairedBraces(button.url)) return say(t, 'BLANK_BRACES');
  const found = placeholders(button.url);
  if (found.length === 0) return '';
  if (found.length > 1) return say(t, 'BUTTON_URL_ONE_BLANK');
  if (!button.url.trimEnd().endsWith('}}')) return say(t, 'BUTTON_URL_AT_END');
  if (!button.url_sample) return say(t, 'BUTTON_URL_SAMPLE');
  return validateBlanks(button.url, draft, t);
};

const validateButton = (button, draft, t) => {
  if (button.type === 'COPY_CODE') {
    if (!button.code) return say(t, 'BUTTON_CODE_BLANK');
    if (button.code.length > LIMITS.COPY_CODE)
      return say(t, 'BUTTON_CODE_LONG');
    return '';
  }
  if (!button.text) return say(t, 'BUTTON_TEXT_BLANK');
  if (button.text.length > LIMITS.BUTTON_TEXT)
    return say(t, 'BUTTON_TEXT_LONG');
  if (button.type === 'URL') return validateUrlButton(button, draft, t);
  if (button.type === 'PHONE_NUMBER') {
    if (!button.phone_number) return say(t, 'BUTTON_PHONE_BLANK');
    if (!/^\+[0-9]{5,}$/.test(button.phone_number)) {
      return say(t, 'BUTTON_PHONE_INVALID');
    }
  }
  return '';
};

/**
 * Meta's rule about the ORDER the buttons are in: quick replies and everything else must form two
 * groups, so `[reply, link, reply]` is refused while `[reply, reply, link]` is fine. No per-button
 * check and no per-type cap can see this, and Graph's refusal says only "invalid combination".
 */
export const buttonsGrouped = buttons => {
  const quick = (buttons ?? [])
    .map((button, index) => (button.type === 'QUICK_REPLY' ? index : -1))
    .filter(index => index !== -1);
  if (quick.length < 2) return true;
  return quick[quick.length - 1] - quick[0] === quick.length - 1;
};

/**
 * What is wrong with the LIST rather than with any one button — the three rules no row can see
 * about itself: too many altogether, too many of one kind, and the quick replies split apart.
 *
 * Split out of `validateButtons` so the screen can put it where it belongs. A per-row problem
 * belongs on the row; these three belong under the whole list, because moving the third button
 * is the fix for a sentence the second button would otherwise be carrying.
 */
const listProblem = (buttons, t) => {
  if (buttons.length > BUTTON_TOTAL_CAP) return say(t, 'BUTTONS_MANY');
  const over = BUTTON_TYPES.find(
    type =>
      buttons.filter(button => button.type === type).length > BUTTON_CAPS[type]
  );
  if (over) return say(t, `BUTTONS_MANY_${over}`);
  if (!buttonsGrouped(buttons)) return say(t, 'BUTTONS_GROUPING');
  return '';
};

/** `listProblem`, from a draft. Same three rules, same sentences, no second spelling. */
export const buttonListProblem = (draft, t) =>
  listProblem((draft?.buttons ?? []).map(buttonToWire), t);

const validateButtons = (wire, draft, t) => {
  const list = listProblem(wire.buttons, t);
  if (list) return list;
  const index = wire.buttons.findIndex(button =>
    validateButton(button, draft, t)
  );
  if (index === -1) return '';
  return say(t, 'BUTTON_AT', {
    number: index + 1,
    reason: validateButton(wire.buttons[index], draft, t),
  });
};

const validateTtl = (draft, t) => {
  if (draft.ttl === '' || draft.ttl === null || draft.ttl === undefined)
    return '';
  const category = trimmed(draft.category).toUpperCase();
  if (!TTL_RANGES[category]) return say(t, 'TTL_NOT_FOR_CATEGORY');
  return ttlAllowed(category, draft.ttl) ? '' : say(t, 'TTL_RANGE');
};

const validateExpiry = (draft, t) => {
  if (!isAuthentication(draft)) return '';
  if (draft.codeExpirationMinutes === '') return '';
  const minutes = Number(draft.codeExpirationMinutes);
  const ok =
    Number.isInteger(minutes) &&
    minutes >= LIMITS.CODE_EXPIRY_MIN &&
    minutes <= LIMITS.CODE_EXPIRY_MAX;
  return ok ? '' : say(t, 'EXPIRY_RANGE');
};

/**
 * `{ field: <one sentence> }`, empty when the draft is submittable. Mirrors
 * `Waslo::TemplateRules.validate`; the server is still the boundary.
 *
 * @param editing {boolean} an edit sends no name and no language — Meta does not accept either
 *   on `POST /{template_id}` — so there is nothing to check and a locked field must never be
 *   able to block a save.
 * @param changeCategory {boolean} whether this edit is asking Meta to RE-PRICE the template.
 *   Only then does the category ride on the wire, and only then is it this form's to judge.
 */
export const validateDraft = (
  draft,
  t,
  editing = false,
  changeCategory = false
) => {
  const wire = draftToWire(draft);
  const errors = {};
  const checks = {
    ttl: validateTtl(draft, t),
    codeExpirationMinutes: validateExpiry(draft, t),
  };
  // ── VALIDATE WHAT IS SENT, NOT WHAT IS CARRIED ────────────────────────────────────
  //
  // The same guard as `Waslo::TemplateRules.category_if_sent`, and the reason is the one
  // written out there: Meta's READ enum is far wider than its creatable one — it still returns
  // `SHIPPING_UPDATE`, `OTP`, `FREE_SERVICE` and a dozen other legacy values on old templates —
  // so a draft read back by `templateToDraft` routinely carries a category `CATEGORIES` has
  // never heard of.
  //
  // This check used to run on every save. On an approved template the select is DISABLED
  // (`categoryLocked`), so the owner could not change the value, could not clear the error and
  // could not save: an old template was locked out of its own editor over a field nobody
  // touched and no control could reach. The server had already decided not to check it. The
  // browser refused anyway — the mirror image of the defect this file's header warns about,
  // and worse, because this end is the one with the Save button.
  if (!editing || changeCategory) {
    checks.category = CATEGORIES.includes(wire.category)
      ? ''
      : say(t, 'CATEGORY_INVALID');
  }
  if (!editing) {
    checks.name = validateName(wire, t);
    checks.language = LANGUAGE_PATTERN.test(wire.language)
      ? ''
      : say(t, wire.language ? 'LANGUAGE_INVALID' : 'LANGUAGE_BLANK');
  }
  if (!isAuthentication(draft)) {
    checks.body = validateBody(wire, draft, t);
    checks.samples = validateSamples(wire, draft, t);
    checks.header = validateHeader(wire, draft, t);
    checks.headerSample =
      placeholders(wire.header).length && !wire.header_sample
        ? say(t, 'HEADER_SAMPLE_MISSING')
        : '';
    checks.footer = validateFooter(wire, t);
    checks.buttons = validateButtons(wire, draft, t);
  }
  Object.entries(checks).forEach(([field, message]) => {
    if (message) errors[field] = message;
  });
  return errors;
};

/**
 * ── A DIGIT IN A NAMED BLANK: THE SECOND THING THIS FILE WARNS ABOUT ────────────────
 *
 * Meta: "Parameters using the named format must be unique, single strings, composed of lowercase
 * characters and underscores, wrapped in double curly brackets, for example, `{{first_name}}`."
 * Lowercase characters and underscores. No digits — so `{{order_2}}`, which this editor and the
 * server both accept, is outside the set Meta documents.
 *
 * IT WARNS. IT DOES NOT REFUSE, and the shape is `duplicateOf`'s, deliberately, for the same
 * class of reason:
 *
 *   • Meta documents the allowed SET but publishes no rejection reason saying a digit is refused.
 *     Every other rule in `validateDraft` is a refusal Meta has WRITTEN DOWN. Inferring one from
 *     an allow-list is a guess, and the cost of a wrong guess falls entirely on the owner: she
 *     cannot save `{{order_2}}` and nothing on the screen can tell her why not.
 *   • `Waslo::TemplateBuilder::NAMED_PATTERN` accepts digits. A client stricter than its server
 *     is the same defect wearing a hat, and that exact divergence locked an owner out of editing
 *     her own template. A refusal here would re-open it from the other side.
 *
 * So `validateDraft` never calls this, the Save button never reads it, and what the owner gets is
 * Meta's own rule plus what it may cost her — which is a wait for a review that comes back
 * refused, not a lost template.
 *
 * A BLANK THAT IS ALREADY REFUSED DOES NOT ALSO WARN. `{{#a1}}` carries a digit and a `#`, and
 * `BODY_NOT_A_NAME` is already naming it; a second sentence about the digit underneath would be
 * telling her about the smaller of her two problems.
 *
 * @param draft the draft being written.
 * @returns the first named blank carrying a digit, or `null`. Numbered drafts always give `null`
 *   — a digit is the entire point of `{{1}}`.
 */
export const nameWithDigit = draft => {
  if (!isNamed(draft)) return null;
  const written = [
    text(draft?.header),
    text(draft?.body),
    ...(draft?.buttons ?? []).map(button => text(button?.url)),
  ];
  const found = written.flatMap(value => placeholderNames(value));
  return (
    found.find(name => NAMED_PATTERN.test(name) && /\d/.test(name)) ?? null
  );
};

/** `nameWithDigit` as the sentence, or `''`. The shape `buttonListProblem` already has. */
export const digitNameWarning = (draft, t) => {
  const name = nameWithDigit(draft);
  return name ? say(t, 'NAME_DIGIT', { name: `{{${name}}}` }) : '';
};

/**
 * One sentence per button, aligned to `draft.buttons` by index — `''` where that button is fine.
 *
 * `validateDraft` reports the buttons as ONE message naming a number ("Button 2: …"), which is
 * the right shape for a refusal read after a Save and the wrong shape for a list of four rows
 * being edited: the sentence sits at the bottom of the section, under whichever row it is about,
 * and only ever describes the first broken one. Here every row carries its own.
 *
 * The rules are the same rules — the same `validateButton` the submit gate runs — so a row that
 * is quiet here cannot be refused there.
 */
export const buttonProblems = (draft, t) =>
  (draft?.buttons ?? []).map(button =>
    validateButton(buttonToWire(button), draft, t)
  );

/**
 * A button row she has added and not yet written anything into.
 *
 * Every one of Meta's per-button rules is a rule about a value, and a row with no values in it
 * breaks all of them at once. Firing four sentences at a row the owner created two hundred
 * milliseconds ago is how as-you-type validation earns its bad name, so a pristine row stays
 * quiet until she types in it — or until she presses Save, when it is genuinely a problem.
 */
export const untouchedButton = button =>
  !trimmed(button?.text) &&
  !trimmed(button?.url) &&
  !trimmed(button?.phoneNumber) &&
  !trimmed(button?.code);

/**
 * The parts that are longer than Meta allows, as `{ part, length, limit }`.
 *
 * Over-length cannot be TYPED any more — every box on the screen carries Meta's own ceiling as
 * its `maxlength` — so this exists for the one way it still happens: a template written in
 * WhatsApp Manager and read back for editing. `templateToDraft` loads it faithfully, which is
 * the whole point of that function, and this is what says so out loud instead of letting the
 * owner discover it when Meta refuses her save.
 */
export const overLimits = draft => {
  const parts = [
    ['HEADER', trimmed(draft?.header), LIMITS.HEADER],
    ['BODY', text(draft?.body), LIMITS.BODY],
    ['FOOTER', trimmed(draft?.footer), LIMITS.FOOTER],
  ];
  (draft?.buttons ?? []).forEach(button => {
    if (button.type !== 'COPY_CODE') {
      parts.push(['BUTTON', trimmed(button.text), LIMITS.BUTTON_TEXT]);
    }
  });
  return parts
    .filter(([, value, limit]) => value.length > limit)
    .map(([part, value, limit]) => ({
      part: part,
      length: value.length,
      limit: limit,
      over: value.length - limit,
    }));
};

/**
 * ── VALIDATING AS SHE TYPES, WITHOUT SHOUTING AT A FORM SHE HAS NOT FILLED IN ───────
 *
 * Every rule in `validateDraft` used to wait for the Save button, so a twelve-field form
 * answered a first-time owner with twelve refusals at once, twenty minutes after the mistake
 * that caused the first of them. Running the same rules on every keystroke is the fix, and it
 * has exactly two failure modes, both of which this function exists to avoid:
 *
 *   1. A BLANK FIELD NOBODY HAS TOUCHED IS NOT A MISTAKE. A fresh form has no name, no language
 *      and no message, and painting all three ruby on first paint is the defect `WasloInput`'s
 *      own header already records from a different direction. So a field's sentence is held
 *      until she has touched it — or until she presses Save, when every rule speaks.
 *   2. A VALUE BEING TYPED IS NOT A FINISHED VALUE. See `openingBlank`.
 *
 * TOUCH IS INHERITED, and that is the part that does the work. An example belongs to the body
 * that created the blank, so writing `{{1}}` engages the example box underneath it and "give an
 * example for every blank" arrives with the box rather than twenty minutes later. A button's
 * label belongs to the button, so typing its address engages its label.
 *
 * @param errors the map `validateDraft` returned. This function NEVER adds to it — the submit
 *   gate and the screen run one rule set, and a client stricter than its server is the defect
 *   `template-draft.js`'s own header warns about, one layer further in.
 * @param draft the draft, for the still-typing test.
 * @param touched `{ field: true }` — the fields she has typed in.
 * @param tried whether Save has been pressed. Then everything speaks.
 */
export const TOUCH_INHERITS = Object.freeze({
  samples: 'body',
  headerSample: 'header',
});

export const typingErrors = (
  errors,
  draft,
  { touched = {}, tried = false } = {}
) => {
  if (tried) return { ...errors };
  const engaged = field =>
    touched[field] === true || touched[TOUCH_INHERITS[field]] === true;
  const stillTyping = {
    body: () => openingBlank(draft?.body),
    header: () => openingBlank(draft?.header),
    footer: () => openingBlank(draft?.footer),
    buttons: () =>
      (draft?.buttons ?? []).some(button => openingBlank(button.url)),
  };
  const shown = {};
  Object.entries(errors).forEach(([field, message]) => {
    if (!engaged(field)) return;
    if (stillTyping[field]?.()) return;
    shown[field] = message;
  });
  return shown;
};

/**
 * ── ONE-PRESS FIXES ─────────────────────────────────────────────────────────────────
 *
 * `TemplateButtonList.regroup()` was the only one of these in the product: a rule Meta reports
 * as "invalid combination" and nothing else, with its fix attached to the diagnosis. The two
 * below are the same trade — a rule that is mechanical to state and mechanical to satisfy, so
 * the screen satisfies it rather than asking the owner to.
 *
 * The line neither of them crosses: a fix may only REARRANGE what she wrote. Neither invents a
 * word, a number or a punctuation mark that will be sent to a customer.
 */

/** `{{1}} {{3}}` → `{{1}} {{2}}`. Meta refuses a gap; renumbering in place is the whole fix. */
export const renumberBlanks = body => {
  const order = placeholderNumbers(body).filter(number => number !== null);
  if (!order.length) return text(body);
  const moved = new Map(order.map((number, index) => [number, index + 1]));
  return text(body).replace(VARIABLE_PATTERN, (whole, inner) => {
    const raw = String(inner).trim();
    if (!/^\d+$/.test(raw)) return whole;
    return `{{${moved.get(Number(raw)) ?? raw}}}`;
  });
};

/** Whether renumbering would change anything — numbered blanks that are not already 1…n. */
export const canRenumber = (body, draft = null) => {
  if (draft && isNamed(draft)) return false;
  const numbers = placeholderNumbers(body);
  if (numbers.includes(null) || numbers.length === 0) return false;
  return numbers.some((number, index) => number !== index + 1);
};

/**
 * `Order Ready v1` → `order_ready_v1`. Meta allows small letters, digits and underscores, and
 * refuses anything else — which an owner discovers by typing her own shop's name.
 *
 * Returns `''` when there is nothing it can honestly offer: an Arabic name transliterates to
 * nothing here, and inventing a Latin name for an Arabic shop would be a guess she then has to
 * live with for thirty days if she ever deletes it.
 */
export const tidyName = value =>
  text(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, LIMITS.NAME);

export const canTidyName = value => {
  const raw = trimmed(value);
  if (!raw) return false;
  const tidy = tidyName(raw);
  return tidy !== '' && tidy !== raw;
};

/** The five goals of "Start from a goal" (P-4.5), in the server's order (`TEMPLATE_GOALS`). */
export const TEMPLATE_GOALS = Object.freeze([
  'appointment_reminder',
  'order_ready',
  'back_in_stock',
  'offer',
  'custom',
]);

/**
 * ── ONE HALF OF A DRAFT FROM A GOAL, AS THIS EDITOR'S OWN DRAFT (P-4.5) ──────────────
 *
 * The server writes both languages in one answer. The editor holds ONE template at a time (a
 * name plus a language), so this takes the half for `lang` and nothing else: the other half is
 * kept by the page for "Make the Arabic one too", which costs no second call.
 *
 * The blanks are NAMED (`{{customer_name}}`), so the samples are laid out in the order the body
 * first uses each name, which is how `sampleCount`, `bodyExample` and the preview read them. The
 * name goes through `tidyName` because the editor's own rule would refuse anything else. Quick
 * replies become ordinary quick-reply rows she can edit or delete. Nothing is validated here:
 * the page runs `validateDraft` like it does for anything she types.
 *
 * @param answer the BFF's `draft` (`api/client.js` `readTemplateDraft`).
 * @param lang `'en' | 'ar'`: which half.
 * @param language the draft's `language` field; defaults to `lang`, keeps an `en_US` she typed.
 */
export const goalDraftToDraft = (answer, lang, language = lang) => {
  const half = lang === 'ar' ? answer?.ar : answer?.en;
  const body = foldBlankDigits(half?.body);
  const sampleOf = new Map(
    (answer?.blanks ?? []).map(blank => [
      blank.name,
      text(lang === 'ar' ? blank.sampleAr : blank.sampleEn),
    ])
  );
  return fitSamples({
    ...blankDraft(),
    name: tidyName(answer?.name),
    language: language,
    category: answer?.category === 'MARKETING' ? 'MARKETING' : 'UTILITY',
    parameterFormat: 'NAMED',
    body: body,
    footer: text(half?.footer),
    samples: placeholderNames(body).map(name => sampleOf.get(name) ?? ''),
    buttons: (half?.quickReplies ?? []).map(reply =>
      buttonRow({ type: 'QUICK_REPLY', text: text(reply) })
    ),
  });
};

/**
 * ── WHAT CHOOSING A CATEGORY WILL DO TO THE SCREEN, BEFORE IT DOES IT ───────────────
 *
 * Choosing a sign-in code takes seven fields off this form and puts two new ones there. It is
 * the largest state change in the product and it used to happen the instant the select changed,
 * silently, with the owner's wording simply gone from view — so she either believed she had
 * lost it or she did not notice until the preview changed under her.
 *
 * Nothing IS lost. The draft keeps every field; `draftToWire` declines to SEND the ones Meta
 * writes itself, and picking the old category back brings the whole form back. That is a
 * promise worth making only because it is true — and `DESIGN-BRIEF`'s offender #2 is a promise
 * of exactly this shape that was not.
 *
 * The one thing that really is cleared is the delivery window, because Meta's ranges differ per
 * category and a window the new category cannot carry is dropped rather than stranded (see the
 * watcher in `TemplateEditorPage`). So it is named separately, under its own word.
 *
 * @returns `{ hides, shows, clears }` — arrays of part names, each one something she can SEE.
 *   Empty arrays mean the change costs her nothing, which is the ordinary case on a blank form.
 */
export const categorySwitch = (draft, next) => {
  const from = trimmed(draft?.category).toUpperCase();
  const to = trimmed(next).toUpperCase();
  const written = [];
  if (trimmed(draft?.header)) written.push('HEADER');
  if (trimmed(draft?.body)) written.push('BODY');
  if (trimmed(draft?.footer)) written.push('FOOTER');
  if ((draft?.buttons ?? []).length) written.push('BUTTONS');
  const authored = [];
  if (draft?.codeExpirationMinutes !== '') authored.push('EXPIRY');
  const clears =
    draft?.ttl !== '' && !ttlAllowed(to, draft?.ttl) ? ['WINDOW'] : [];
  if (from === to) return { hides: [], shows: [], clears: [] };
  if (to === 'AUTHENTICATION') {
    return { hides: written, shows: ['SECURITY', 'EXPIRY'], clears: clears };
  }
  if (from === 'AUTHENTICATION') {
    return { hides: authored, shows: written, clears: clears };
  }
  return { hides: [], shows: [], clears: clears };
};

/** Whether the switch is worth stopping for, or is free and should just happen. */
export const switchCosts = change =>
  change.hides.length > 0 || change.clears.length > 0;

/**
 * ── THE FORMAT IS A CONSEQUENCE OF WHAT SHE WROTE, NOT A QUESTION ───────────────────
 *
 * Meta's `parameter_format` says whether a template's blanks are `{{1}}` or `{{order_id}}`. It
 * was a select at the top of the message section, above the message it describes, and it is the
 * one control on this screen that an owner cannot answer from anything in her own shop: both
 * options do the same thing, the difference is visible only to whoever fills the blanks in
 * later, and choosing the one that disagrees with what she then types produces a refusal whose
 * sentence points at the body.
 *
 * So it is read off the blanks instead, which is what the field MEANS. Two numbered blanks make
 * a numbered template; two named ones make a named template; a body with no blanks at all keeps
 * whatever the draft already carries. A MIXED body is left alone deliberately — that is a real
 * mistake with a rule of its own (`BODY_NAMED_BLANK`), and quietly picking a format for it would
 * swap a sentence naming the problem for a refusal naming nothing.
 *
 * NEVER ON AN EDIT. Meta's own two reference pages disagree about whether the format may change
 * on an approved template, so the editor locks it to whatever `templateToDraft` read back and
 * this function is not called at all.
 */
export const inferFormat = draft => {
  const current = isNamed(draft) ? 'NAMED' : 'POSITIONAL';
  const found = placeholders(draft?.body);
  const blanks = found.length ? found : placeholders(draft?.header);
  if (!blanks.length) return current;
  if (blanks.every(blank => /^\d+$/.test(blank))) return 'POSITIONAL';
  if (blanks.every(blank => NAMED_PATTERN.test(blank))) return 'NAMED';
  return current;
};

const readHeaderSample = (header, named) => {
  if (!header) return '';
  if (named)
    return text(header.example?.header_text_named_params?.[0]?.example);
  return text(header.example?.header_text?.[0]);
};

const readSamples = (body, named) => {
  if (!body) return [];
  if (named) {
    const pairs = body.example?.body_text_named_params;
    return Array.isArray(pairs) ? pairs.map(pair => text(pair?.example)) : [];
  }
  const values = body.example?.body_text?.[0];
  return Array.isArray(values) ? values.map(value => text(value)) : [];
};

const readUrlSample = (url, example) => {
  const blank = url.indexOf('{{');
  if (blank === -1 || !example) return '';
  const prefix = url.slice(0, blank);
  return example.startsWith(prefix) ? example.slice(prefix.length) : example;
};

/**
 * The buttons, back into rows.
 *
 * A BUTTON THIS EDITOR CANNOT WRITE NEVER REACHES HERE ANY MORE. It used to: the row was kept,
 * flagged `readOnly`, and the form claimed it "survives the round trip untouched" — which was
 * false, because `buttonToWire` never read the stored original, so a FLOW button went back to
 * Meta as `{type:'FLOW', text:'…'}` with its `flow_id` gone. Meta refused the save, and the
 * owner's only way out was to delete her own button.
 *
 * The honest fix is upstream of here: `editBlockers` (`template-facts.js`) refuses to open the
 * editor at all for a template carrying one, and says which button and where to edit it. So this
 * function maps what it can and keeps no fiction about the rest.
 */
const readButtons = component => {
  const list = Array.isArray(component?.buttons) ? component.buttons : [];
  return list.map(button => {
    const type = String(button?.type ?? '').toUpperCase();
    const sample = Array.isArray(button.example)
      ? text(button.example[0])
      : text(button.example);
    return buttonRow({
      type: type,
      text: text(button.text),
      url: text(button.url),
      // Meta's URL example is the WHOLE substituted address; the form wants only the value that
      // goes in the blank, which is what is left after the fixed part of the url.
      urlSample: readUrlSample(text(button.url), sample),
      phoneNumber: text(button.phone_number),
      code: type === 'COPY_CODE' ? sample : '',
    });
  });
};

/**
 * A template Meta already holds, turned back into an editable draft.
 *
 * ── WHY THIS IS THE RISKIEST FUNCTION HERE ──────────────────────────────────────────
 *
 * Meta's edit endpoint REPLACES ALL COMPONENTS with whatever the request carries: "You cannot
 * edit individual template components; the API replaces all components with those in the edit
 * request payload." So anything this function fails to read back is not merely missing from the
 * form — it is DELETED FROM THE LIVE TEMPLATE the moment the owner fixes a typo and saves. A
 * dropped footer, a lost button, an example value silently blanked: each one is a template that
 * goes back into review having quietly lost a piece of itself.
 *
 * That is why the example values are read out of Meta's own example blocks rather than left
 * empty: a template with blanks and no examples is REFUSED, so re-submitting one whose examples
 * were not read back would fail review for a reason the owner never touched.
 */
export const templateToDraft = template => {
  const parts = Array.isArray(template?.components) ? template.components : [];
  const at = wanted =>
    parts.find(part => String(part?.type ?? '').toUpperCase() === wanted);
  const header = at('HEADER');
  const body = at('BODY');
  const footer = at('FOOTER');
  const buttons = at('BUTTONS');
  const named =
    String(template?.parameterFormat ?? '').toUpperCase() === 'NAMED';
  const draft = {
    ...blankDraft(),
    name: text(template?.name),
    language: text(template?.language),
    category: String(template?.category ?? 'UTILITY').toUpperCase(),
    parameterFormat: named ? 'NAMED' : 'POSITIONAL',
    ttl:
      template?.messageSendTtlSeconds === null ||
      template?.messageSendTtlSeconds === undefined
        ? ''
        : String(template.messageSendTtlSeconds),
  };
  if (draft.category === 'AUTHENTICATION') {
    return {
      ...draft,
      addSecurityRecommendation: body?.add_security_recommendation === true,
      codeExpirationMinutes:
        footer?.code_expiration_minutes === undefined ||
        footer?.code_expiration_minutes === null
          ? ''
          : String(footer.code_expiration_minutes),
    };
  }
  return {
    ...draft,
    header: foldBlankDigits(header?.text),
    headerSample: readHeaderSample(header, named),
    body: foldBlankDigits(body?.text),
    footer: text(footer?.text),
    samples: readSamples(body, named),
    buttons: readButtons(buttons),
  };
};

const WIRE_FIELDS = {
  header_sample: 'headerSample',
  message_send_ttl_seconds: 'ttl',
  code_expiration_minutes: 'codeExpirationMinutes',
  parameter_format: 'parameterFormat',
};

/**
 * The server's `[{ field, code, index? }]` turned into the same `{ field: sentence }` map.
 *
 * WHY THE SERVER SENDS CODES AND NOT SENTENCES: an English sentence authored in Ruby is how an
 * Arabic owner ends up reading English mid-form — the finding `specs/vocabulary.spec.js`
 * records. A code this map does not know falls back to one general sentence rather than
 * rendering the code, because a code on screen is worse than a vague sentence.
 */
export const wireErrorsToFields = (errors, t) => {
  const fields = {};
  (errors ?? []).forEach(({ field, code, index }) => {
    if (!field) return;
    const key = errorKey(`WIRE_${String(code).toUpperCase()}`);
    const sentence = t(key, BLANK_SAMPLES);
    const known = sentence && sentence !== key;
    const message = known ? sentence : say(t, 'WIRE_UNKNOWN');
    // The server names its fields in snake_case (`header_sample`); the form's keys are camel.
    const target = WIRE_FIELDS[field] ?? field;
    fields[target] =
      typeof index === 'number'
        ? say(t, 'BUTTON_AT', { number: index + 1, reason: message })
        : message;
  });
  return fields;
};
